import React, { useState } from "react";
import { StaticSelectInput } from "../../constant/SelectInput";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { createDocumentApi } from "../../../services/DocumentService";
import { Tab, Nav } from "react-bootstrap";
import RichTextEditorComponent from "../../constant/RichTextEditor";
import WordFileUpload from "../../constant/FileUpload";

const DocumentAddModal = () => {
  const [dataInput, setDataInput] = useState({
    title: "",
    document_type: "",
    description: "",
    document_data: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState("");
  const navigate = useNavigate();
  const [activeKey, setActiveKey] = useState("newDocument");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setDataInput((prevDataInput) => ({ ...prevDataInput, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setDataInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (newValue) => {
    setDataInput((prevDataInput) => ({
      ...prevDataInput,
      document_data: newValue,
    }));
  };

  const handleFileSelect = (file) => {
    setDataInput((prevDataInput) => ({
      ...prevDataInput,
      document_data: file,
    }));
  };

  const handleTagInput = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      const newTag = tagInput.trim();

      const tagList = tags.split(", ");
      if (tagList.includes(newTag)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          tags: "Tag already exists",
        }));
        return;
      }
      const updatedTags = tags ? `${tags}, ${newTag}` : newTag;
      setTags(updatedTags);
      setTagInput("");
      setErrors((prevErrors) => ({
        ...prevErrors,
        tags: "",
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    const updatedTags = tags
      .split(", ")
      .filter((tag) => tag !== tagToRemove)
      .join(", ");
    setTags(updatedTags);
  };

  const validateInputs = () => {
    const validationErrors = {};
    if (!dataInput.title) validationErrors.title = "Document Title is required";
    if (!dataInput.description)
      validationErrors.description = "Description is required";
    if (!tags) validationErrors.tags = "At least one tag is required";

    return validationErrors;
  };

  const handleSubmit = async (isDraft) => {
    const validationErrors = validateInputs();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitLoading(true);
      const formData = new FormData();
      formData.append("title", dataInput.title);
      formData.append("document_type", dataInput.document_type);
      formData.append("description", dataInput.description);
      formData.append("tags", tags);
      if (dataInput.document_data instanceof File) {
        formData.append("file", dataInput.document_data);
      } else {
        formData.append("content", dataInput.document_data);
      }
      formData.append("is_draft", isDraft);

      const response = await createDocumentApi(formData);
      toast.success(response.data.message);
      navigate("/document");
    } catch (error) {
      toast.error(error.response?.data?.message);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setDataInput({
      title: "",
      document_type: "",
      description: "",
      document_data: "",
    });
    setErrors({});
    setTags("");
    navigate("/document");
  };

  return (
    <div className="container-fluid">
      <div className="row px-sm-2 px-0">
        <div className="col-xl-12">
          <div className="card px-sm-3 px-1">
            <div className="card-body">
              <div className="d-flex align-items-center mb-4">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm btn-circle"
                  onClick={() => navigate("/document")}
                >
                  <i className="fa-solid fa-arrow-left"></i>
                </button>
                <h3 className="mb-0 ms-2">Document</h3>
              </div>
              <form>
                <div className="row mb-2">
                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <StaticSelectInput
                        optionsList={[
                          { label: "Quality Manual", value: "QM" },
                          { label: "Quality System Procedure", value: "QSP" },
                          { label: "Quality Plan", value: "QP" },
                          { label: "Process Detail", value: "PD" },
                          { label: "Work Instruction", value: "WI" },
                          { label: "Form", value: "FORM" },
                          {
                            label:
                              "Specification and Standards of External Origin",
                            value: "SSE",
                          },
                          { label: "Statistical Tech Doc", value: "STD" },
                        ]}
                        setParentInputValue={handleSelectChange}
                        name="document_type"
                        value={dataInput.document_type}
                        isClearable
                        placeholder="Select Document Type"
                        label="Document Type"
                        isRequired={true}
                      />
                      {errors.document_type && (
                        <div className="text-danger">
                          {errors.document_type}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Title<span className="text-danger">*</span>
                      </label>
                      <input
                        className="form-control"
                        placeholder="Enter Title"
                        name="title"
                        value={dataInput.title}
                        onChange={handleChange}
                      />
                      {errors.title && (
                        <div className="text-danger">{errors.title}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group mb-3">
                      <label className="form-label">
                        Description<span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        placeholder="Enter Description"
                        name="description"
                        value={dataInput.description}
                        onChange={handleChange}
                      />
                      {errors.description && (
                        <div className="text-danger">{errors.description}</div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 mb-2">
                    <div className="d-flex align-items-center mb-2">
                      <label className="form-label me-3">
                        Keyword<span className="text-danger">*</span>
                      </label>
                      <div>
                        {tags &&
                          tags.split(", ").map((tag, index) => (
                            <span
                              key={index}
                              className="badge px-2 py-0 bg-primary text-white me-2 mb-2"
                            >
                              {tag}
                              <span
                                className="text-white ms-2"
                                onClick={() => removeTag(tag)}
                              >
                                <i className="fa-solid fa-xmark"></i>
                              </span>
                            </span>
                          ))}
                      </div>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Add tags (Press Enter)"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagInput}
                    />
                    {errors.tags && (
                      <div className="text-danger mt-1">{errors.tags}</div>
                    )}
                  </div>
                </div>

                <div className="card">
                  <Tab.Container activeKey={activeKey} onSelect={setActiveKey}>
                    <div className="card-header p-0">
                      <Nav variant="tabs">
                        <Nav.Item>
                          <Nav.Link
                            eventKey="newDocument"
                            className="tabnav p-3"
                          >
                            <strong>New Document</strong>
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link
                            eventKey="uploadDocument"
                            className="tabnav p-3"
                          >
                            <strong>Upload Document</strong>
                          </Nav.Link>
                        </Nav.Item>
                      </Nav>
                    </div>
                    <div className="card-body p-0">
                      <Tab.Content>
                        <Tab.Pane eventKey="newDocument">
                          {activeKey === "newDocument" && (
                            <RichTextEditorComponent
                              handleChange={handleEditorChange}
                            />
                          )}
                        </Tab.Pane>
                        <Tab.Pane eventKey="uploadDocument">
                          {activeKey === "uploadDocument" && (
                            <WordFileUpload onFileSelect={handleFileSelect} />
                          )}
                        </Tab.Pane>
                      </Tab.Content>
                    </div>
                  </Tab.Container>
                </div>

                <div className="d-flex align-items-center justify-content-start">
                  <button
                    type="button"
                    className="btn btn-primary me-3"
                    onClick={() => handleSubmit("No")}
                    disabled={isSubmitLoading}
                  >
                    {isSubmitLoading ? "Submitting..." : "Submit"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary me-3"
                    onClick={() => handleSubmit("Yes")}
                    disabled={isSubmitLoading}
                  >
                    Save as Draft
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentAddModal;

// import React, { useState } from 'react';
// import RichTextEditorComponent from '../../constant/RichTextEditor';

// const DocumentAddModal = () => {
//   const [data, setData] = useState("");

//   const handleChange = (newValue) => {
//     setData(newValue);
//   };

//   return (
//     <>
//       <RichTextEditorComponent handleChange={handleChange} />
//     </>
//   );
// };

// export default DocumentAddModal;

// import React, { useState, useRef, useEffect } from "react";

// const DocumentAddModal = () => {
//   const [showFileMenu, setShowFileMenu] = useState(false);
//   const [content, setContent] = useState(null);  // state to hold the editor instance
//   const refDiv = useRef(null);  // reference to the div where the editor will be rendered

//   let editor

//   useEffect(() => {
//     if(refDiv.current){
//       editor = new window.RichTextEditor(refDiv.current);  // initialize RichTextEditor
//     }
//   }, []);

//   editor.attachEvent("change", function () {
//     var textCount = editor.getPlainText().trim().length;
//     document.title = "textCount:" + textCount;
// });

//   // Handle file upload for images
//   const newDocument = () => {
//       setContent(""); // Clear editor content
//       setShowFileMenu(false); // Close the file menu
//   };

//   // Upload Document
//   const uploadDocument = (event) => {
//     const file = event.target.files[0];
//     if (file && (file.type === "text/plain" || file.type === "text/html")) {
//       const reader = new FileReader();
//       reader.onload = () => {
//         setContent(reader.result); // Set the uploaded file content to editor
//       };
//       reader.readAsText(file);
//       setShowFileMenu(false); // Close the file menu
//     } else {
//       alert("Please upload a valid text or HTML file.");
//     }
//   };

//   const submitData = () => {
//     console.log("Submitted Document Content: ", editor.getHTMLCode());
//   };

//   return (
//     <div className="editor-container p-3">
//       <div className="file-menu" style={{ backgroundColor: "white" }}>
//         <button
//           className="btn text-black"
//           onClick={() => setShowFileMenu(!showFileMenu)}
//         >
//           File
//         </button>

//         {/* Dropdown for File Menu */}
//         {showFileMenu && (
//           <div className="file-dropdown">
//             <button onClick={newDocument}>
//               <i className="fa-solid fa-folder-plus menu-icon me-2"></i> New
//               Document
//             </button>
//             <label htmlFor="upload-doc">
//               <i className="fa-solid fa-file-arrow-up menu-icon me-2"></i> Upload
//               Document
//             </label>
//             <input
//               id="upload-doc"
//               type="file"
//               accept=".txt,.html"
//               onChange={uploadDocument}
//               style={{ display: "none" }}
//             />
//           </div>
//         )}
//       </div>
//       <header className="DocumentAddModal-header">
//         <textarea ref={refDiv} onChange={(e) => console.log("value",e.target.value)}>
//           Enter Your Document Here!
//         </textarea>
//       </header>
//       <button onClick={submitData}>Submit Document</button>
//       {/* <button onClick={() => setEditorContent('')}>New Document</button> */}
//       {/* You can add more buttons for saving or exporting content */}
//     </div>
//   );
// };

// export default DocumentAddModal;
